import type { ClinicalReport } from './jsonReportGenerator';
import type {
  AnalysisSummary,
  DiagnosticPattern,
  DiagnosticRecommendation,
} from '../types/diagnostic';
import { ncsStandardTemperatureC } from '../data/emgClinicalCriteria';

export interface DiagnosticAnalysisResult {
  patterns: DiagnosticPattern[];
  recommendations: DiagnosticRecommendation[];
  summary: AnalysisSummary;
}

export interface ClinicalImpression {
  indication: string;
  ncsFindings: string;
  emgFindings: string;
  impression: string;
  clinicalCorrelation: string;
  limitations: string;
  compatiblePatterns: Array<{
    name: string;
    confidence: number;
    urgencyLevel: DiagnosticPattern['urgencyLevel'];
  }>;
  recommendations: string[];
  urgency: ClinicalReport['conclusion']['urgency'];
  confidence: number;
  copySummary: string;
}

const formatPercent = (value: number): string => `${Math.round(value * 100)}%`;

const sentence = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

function mapUrgency(urgency: DiagnosticPattern['urgencyLevel'] | undefined): ClinicalReport['conclusion']['urgency'] {
  if (urgency === 'urgent') return 'urgent';
  if (urgency === 'high') return 'high';
  if (urgency === 'medium') return 'medium';
  return 'low';
}

function buildNcsFindings(report: ClinicalReport): string {
  const ncs = report.electrophysiologicalData.ncs;
  if (!ncs || ncs.results.length === 0) {
    return 'No se registraron estudios de neuroconducción interpretables en el reporte.';
  }

  const abnormal = ncs.results.filter(result => result.status === 'abnormal');
  const absent = ncs.results.filter(result => result.absentResponse);
  const lowTemperature = ncs.results.filter(result => {
    if (result.temperature === undefined || result.temperature === null || !Number.isFinite(result.temperature)) return false;
    return result.temperature < ncsStandardTemperatureC(result.nerve);
  });

  const parts = [
    `Se evaluaron ${ncs.results.length} registro(s) de neuroconducción.`,
    `Patrón global: ${ncs.globalFindings.overallPattern}, distribución ${ncs.globalFindings.distribution}, severidad ${ncs.globalFindings.severity}.`,
  ];

  if (abnormal.length > 0) {
    parts.push(`${abnormal.length} registro(s) fueron anormales: ${abnormal.map(item => `${item.nerve} ${item.side}`).join(', ')}.`);
  } else {
    parts.push('No se documentaron anormalidades electrofisiológicas en los registros NCS disponibles.');
  }

  if (absent.length > 0) {
    parts.push(`Hubo respuesta ausente en ${absent.map(item => `${item.nerve} ${item.side}`).join(', ')}.`);
  }

  if (lowTemperature.length > 0) {
    parts.push('La interpretación debe considerar registros con temperatura cutánea por debajo del estándar mínimo documentado.');
  }

  return parts.join(' ');
}

function buildEmgFindings(report: ClinicalReport): string {
  const emg = report.electrophysiologicalData.emg;
  if (!emg || emg.status === 'nodata') {
    return 'No se registraron datos de electromiografía con aguja interpretables.';
  }
  if (emg.status === 'skipped') {
    return 'La electromiografía con aguja no fue realizada; la impresión se limita a clínica y neuroconducciones disponibles.';
  }

  const abnormalMuscles = emg.muscles.filter(muscle =>
    muscle.insertionalActivity !== 'normal' ||
    (muscle.spontaneousActivitySummary !== 'Ausente' && muscle.spontaneousActivitySummary !== 'Normal') ||
    muscle.recruitmentPattern !== 'normal' ||
    muscle.abnormalityScore > 0
  );

  const parts = [
    `Se examinaron ${emg.muscles.length} músculo(s) con aguja.`,
    `Patrón global: ${emg.globalFindings.overallPattern}, distribución ${emg.globalFindings.distribution}, severidad ${emg.globalFindings.severity}, cronicidad ${emg.globalFindings.chronicity}.`,
  ];

  if (abnormalMuscles.length > 0) {
    parts.push(`Los hallazgos anormales se observaron en ${abnormalMuscles.map(muscle => `${muscle.muscleName} ${muscle.side}`).join(', ')}.`);
  } else {
    parts.push('No se documentaron anormalidades por músculo en la muestra EMG disponible.');
  }

  return parts.join(' ');
}

function buildRecommendations(
  report: ClinicalReport,
  analysis?: DiagnosticAnalysisResult
): string[] {
  const fromReport = report.conclusion.recommendations || [];
  const sortedRecs = (analysis?.recommendations || [])
    .slice()
    .sort((a, b) => b.overallConfidence - a.overallConfidence);

  // Urgent/high-priority pattern recommendations float to the top
  const urgentSteps = sortedRecs
    .filter(r => {
      const p = analysis?.patterns?.find(p => p.id === r.diagnosisId);
      return p?.urgencyLevel === 'urgent' || p?.urgencyLevel === 'high';
    })
    .flatMap(r => r.nextSteps || [])
    .filter(Boolean);

  const allFromAnalysis = sortedRecs.flatMap(r => r.nextSteps || []).filter(Boolean);
  return Array.from(new Set([...urgentSteps, ...fromReport, ...allFromAnalysis])).slice(0, 8);
}

function hasAnyClinicalSymptom(report: ClinicalReport): boolean {
  return Object.values(report.clinicalSymptoms || {}).some((section: any) =>
    Array.isArray(section?.symptoms) && section.symptoms.some((symptom: any) => symptom.present)
  );
}

function adjustedConfidence(
  report: ClinicalReport,
  topPattern: DiagnosticPattern | undefined
): number {
  const base = topPattern?.confidence ?? report.conclusion.confidence ?? 0.3;
  const clinicalSignal = hasAnyClinicalSymptom(report);
  const analysisHasScores = (report.analysis.combinedDiagnosticScore || []).length > 0;
  const cap = !clinicalSignal && !analysisHasScores ? 0.75 : 1;
  return Number(Math.min(base, cap).toFixed(2));
}

export function buildClinicalImpression(
  report: ClinicalReport,
  analysis?: DiagnosticAnalysisResult
): ClinicalImpression {
  const redFlags = report.analysis.redFlags || [];
  const urgentRedFlag = redFlags.find(flag => flag.urgency === 'urgent');
  const highRedFlag = redFlags.find(flag => flag.urgency === 'high');
  const topPattern = analysis?.patterns?.[0];
  const topConfidence = adjustedConfidence(report, topPattern);
  const compatiblePatterns =
    analysis?.patterns.slice(0, 5).map(pattern => ({
      name: pattern.name,
      confidence: pattern.confidence,
      urgencyLevel: pattern.urgencyLevel,
    })) || [];

  const urgency = urgentRedFlag
    ? 'urgent'
    : highRedFlag
      ? 'high'
      : mapUrgency(topPattern?.urgencyLevel) || report.conclusion.urgency;

  const primaryText = urgentRedFlag
    ? `Hallazgo de atención urgente: ${urgentRedFlag.flag}.`
    : highRedFlag
      ? `Hallazgo de alta prioridad: ${highRedFlag.flag}.`
      : topPattern
        ? `El patrón electroclínico es compatible principalmente con ${topPattern.name} (${formatPercent(topConfidence)} de confianza del sistema).`
        : sentence(report.conclusion.primaryDiagnosis);

  const uncertaintyText =
    compatiblePatterns.length > 1
      ? `Diagnósticos/patrones adicionales compatibles: ${compatiblePatterns
          .slice(1, 4)
          .map(pattern => `${pattern.name} (${formatPercent(pattern.confidence)})`)
          .join(', ')}.`
      : '';

  const recommendations = buildRecommendations(report, analysis);
  const limitations = report.metadata.limitations.present
    ? report.metadata.limitations.description
    : 'Sin limitaciones técnicas relevantes documentadas.';
  const clinicalCorrelation =
    'Correlacionar con la historia clínica, exploración neurológica y estudios de imagen/laboratorio cuando el patrón lo requiera. La interpretación integra NCS, EMG y contexto clínico disponible.';

  const impression = [primaryText, uncertaintyText].filter(Boolean).join(' ');
  const indication = report.patient.consultReason || 'Indicación clínica no especificada.';
  const ncsFindings = buildNcsFindings(report);
  const emgFindings = buildEmgFindings(report);

  const copySummary = [
    'IMPRESIÓN DIAGNÓSTICA',
    sentence(impression),
    '',
    'Hallazgos NCS:',
    sentence(ncsFindings),
    '',
    'Hallazgos EMG:',
    sentence(emgFindings),
    '',
    'Correlación clínica:',
    sentence(clinicalCorrelation),
    '',
    'Limitaciones:',
    sentence(limitations),
    '',
    recommendations.length > 0 ? 'Recomendaciones:' : '',
    ...recommendations.map(rec => `- ${sentence(rec)}`),
  ].filter(line => line !== '').join('\n');

  return {
    indication,
    ncsFindings,
    emgFindings,
    impression,
    clinicalCorrelation,
    limitations,
    compatiblePatterns,
    recommendations,
    urgency,
    confidence: topConfidence,
    copySummary,
  };
}

export function applyClinicalImpressionToReport(
  report: ClinicalReport,
  analysis?: DiagnosticAnalysisResult
): ClinicalReport {
  const clinicalImpression = buildClinicalImpression(report, analysis);

  return {
    ...report,
    conclusion: {
      ...report.conclusion,
      primaryDiagnosis: clinicalImpression.impression,
      confidence: clinicalImpression.confidence,
      recommendations: clinicalImpression.recommendations,
      urgency: clinicalImpression.urgency,
      differentialDiagnoses: clinicalImpression.compatiblePatterns.slice(1).map(pattern => pattern.name),
    },
  };
}
